/// <amd-module name="Build/Admin/Pool/Stores/StoresHub" />
import { ChecksStore } from "Build/Admin/Checks/Stores/ChecksStore";
import { AgentDefinitionStore } from "Build/Admin/Common/Stores/AgentDefinitionStore";
import { ConfirmationDialogStore } from "Build/Admin/Common/Stores/ConfirmationDialogStore";
import { JobStore } from "Build/Admin/Common/Stores/JobStore";
import { NotificationStore } from "Build/Admin/Common/Stores/NotificationStore";
import { ProjectStore } from "Build/Admin/Common/Stores/ProjectStore";
import { ResourceLimitStore } from "Build/Admin/Common/Stores/ResourceLimitStore";
import { ServiceEndpointStore } from "Build/Admin/Common/Stores/ServiceEndpointStore";
import { SignalRStore } from "Build/Admin/Common/Stores/SignalRStore";
import { VirtualMachineScaleSetStore } from "Build/Admin/Common/Stores/VirtualMachineScaleSetStore";
import { ActionsHub } from "Build/Admin/Pool/Actions/ActionsHub";
import { AgentStore } from "Build/Admin/Pool/Stores/AgentStore";
import { DescriptionStore } from "Build/Admin/Pool/Stores/DescriptionStore";
import { DownloadAgentDialogStore } from "Build/Admin/Pool/Stores/DownloadAgentDialogStore";
import { ElasticNodeStore } from "Build/Admin/Pool/Stores/ElasticNodeStore";
import { ElasticPoolLogStore } from "Build/Admin/Pool/Stores/ElasticPoolLogStore";
import { MaintenanceJobStore } from "Build/Admin/Pool/Stores/MaintenanceJobsStore";
import { MaintenanceStore } from "Build/Admin/Pool/Stores/MaintenanceStore";
import { PoolAnalyticsStore } from "Build/Admin/Pool/Stores/PoolAnalyticsStore";
import { SecurityStore } from "Build/Admin/Pool/Stores/SecurityStore";
import { SelectionStore } from "Build/Admin/Pool/Stores/SelectionStore";
import { TopPipelinesStore } from "Build/Admin/Pool/Stores/TopPipelinesStore";
import { TaskAgent, TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";

export class StoresHub {
    public agentStore: AgentStore;
    public agentDefinitionStore: AgentDefinitionStore;
    public checksStore: ChecksStore;
    public confirmationDialogStore: ConfirmationDialogStore<TaskAgent | TaskAgentQueue>;
    public descriptionStore: DescriptionStore;
    public downloadAgentDialogStore: DownloadAgentDialogStore;
    public elasticNodeStore: ElasticNodeStore;
    public elasticPoolLogStore: ElasticPoolLogStore;
    public jobStore: JobStore;
    public maintenanceStore: MaintenanceStore;
    public maintenanceJobStore: MaintenanceJobStore;
    public notificationStore: NotificationStore;
    public poolAnalyticsStore: PoolAnalyticsStore;
    public projectStore: ProjectStore;
    public resourceLimitStore: ResourceLimitStore;
    public securityStore: SecurityStore;
    public selectionStore: SelectionStore;
    public serviceEndpointStore: ServiceEndpointStore;
    public signalRStore: SignalRStore;
    public topPipelinesStore: TopPipelinesStore;
    public virtualMachineScaleSetStore: VirtualMachineScaleSetStore;

    constructor(actionsHub: ActionsHub) {
        this.agentStore = new AgentStore();
        actionsHub.agentsUpdated.addListener(this.agentStore.updateAgents);
        actionsHub.agentActions.agentUpdated.addListener(this.agentStore.updateAgent);
        actionsHub.agentDeleted.addListener(this.agentStore.agentDeleted);
        actionsHub.agentActions.agentStatusUpdated.addListener(payload => this.agentStore.updateAgentStatus(payload.agentId, payload.status));
        actionsHub.jobActions.jobUpdated.addListener(this.agentStore.updateAssignedJobRequest);

        this.agentDefinitionStore = new AgentDefinitionStore();
        actionsHub.agentDefinitionsUpdated.addListener(this.agentDefinitionStore.updateAgentDefinitions);

        this.confirmationDialogStore = new ConfirmationDialogStore<TaskAgent | TaskAgentQueue>();
        actionsHub.confirmationDialogActions.showConfirmationDialog.addListener(this.confirmationDialogStore.showDialog);
        actionsHub.confirmationDialogActions.dismissConfirmationDialog.addListener(this.confirmationDialogStore.dismissDialog);

        this.descriptionStore = new DescriptionStore();
        actionsHub.agentPoolDescriptionUpdated.addListener(this.descriptionStore.updateAgentPoolDescription);
        actionsHub.descriptionStoreStatusUpdated.addListener(this.descriptionStore.updateStatus);

        this.downloadAgentDialogStore = new DownloadAgentDialogStore();
        actionsHub.dismissDownloadAgentDialog.addListener(this.downloadAgentDialogStore.dismissDialog);
        actionsHub.showDownloadAgentDialog.addListener(this.downloadAgentDialogStore.showDialog);

        this.elasticNodeStore = new ElasticNodeStore();
        actionsHub.elasticNodesUpdated.addListener(this.elasticNodeStore.updateElasticNodes);
        actionsHub.elasticNodeUpdated.addListener(this.elasticNodeStore.updateElasticNode);
        actionsHub.elasticNodeStoreStatusUpdated.addListener(this.elasticNodeStore.updateStatus);

        this.elasticPoolLogStore = new ElasticPoolLogStore();
        actionsHub.elasticPoolLogsUpdated.addListener(this.elasticPoolLogStore.updateElasticPoolLogs);
        actionsHub.elasticPoolLogStoreStatusUpdated.addListener(this.elasticPoolLogStore.updateStatus);

        this.jobStore = new JobStore();
        actionsHub.jobActions.jobUpdated.addListener(this.jobStore.updateJob);
        actionsHub.jobActions.jobsUpdated.addListener(this.jobStore.updateJobs);

        this.maintenanceStore = new MaintenanceStore();
        actionsHub.maintenanceDefinitionAdded.addListener(this.maintenanceStore.addMaintenanceDefinition);
        actionsHub.maintenanceDefinitionUpdated.addListener(this.maintenanceStore.updateMaintenanceDefinition);
        actionsHub.maintenanceDefinitionsUpdated.addListener(this.maintenanceStore.updateMaintenanceDefinitions);
        actionsHub.maintenanceStoreStatusUpdated.addListener(this.maintenanceStore.updateStatus);
        actionsHub.maintenanceTimeZonesUpdated.addListener(this.maintenanceStore.updateValidTimeZones);

        this.maintenanceJobStore = new MaintenanceJobStore();
        actionsHub.maintenanceJobUpdated.addListener(this.maintenanceJobStore.updateJob);
        actionsHub.maintenanceJobsUpdated.addListener(this.maintenanceJobStore.updateJobs);
        actionsHub.maintenanceJobsStoreStatusUpdated.addListener(this.maintenanceJobStore.updateStatus);

        this.notificationStore = new NotificationStore();
        actionsHub.notificationActions.addNotification.addListener(this.notificationStore.addNotification);
        actionsHub.notificationActions.dismissNotification.addListener(this.notificationStore.dismissNotification);
        actionsHub.notificationActions.clearNotifications.addListener(this.notificationStore.clearNotifications);

        this.projectStore = new ProjectStore();
        actionsHub.projectActions.projectsUpdated.addListener(this.projectStore.updateProjects);

        this.resourceLimitStore = new ResourceLimitStore();
        actionsHub.resourceLimitActions.resourceLimitsUpdated.addListener(this.resourceLimitStore.resourceLimitsUpdated);
        actionsHub.resourceLimitActions.resourceLimitsStatusUpdated.addListener(this.resourceLimitStore.resourceLimitsStatusUpdated);

        this.securityStore = new SecurityStore();
        actionsHub.securityStoreStatusUpdated.addListener(this.securityStore.updateStatus);
        actionsHub.manageQueuePermissionUpdated.addListener(this.securityStore.updateManageQueuePermission);
        actionsHub.managePoolPermissionUpdated.addListener(this.securityStore.updateManagePoolPermission);
        actionsHub.manageAllQueuesPermissionUpdated.addListener(this.securityStore.updateManageAllQueuesPermission);
        actionsHub.manageAllPoolsPermissionUpdated.addListener(this.securityStore.updateManageAllPoolsPermission);
        actionsHub.agentQueuePipelinesAuthorizedUpdated.addListener(this.securityStore.updatePipelinesAuthorizedForQueue);

        this.selectionStore = new SelectionStore();
        actionsHub.selectedAgentPoolUpdated.addListener(this.selectionStore.updateSelectedAgentPool);
        actionsHub.selectedAgentQueueUpdated.addListener(this.selectionStore.updateSelectedAgentQueue);
        actionsHub.selectedElasticPoolUpdated.addListener(this.selectionStore.updateSelectedElasticPool);
        actionsHub.selectedServiceEndpointUpdated.addListener(this.selectionStore.updateSelectedServiceEndpoint);
        actionsHub.selectedVirtualMachineScaleSetUpdated.addListener(this.selectionStore.updateSelectedVirtualMachineScaleSet);
        actionsHub.selectedTabUpdated.addListener(this.selectionStore.updateSelectedTab);

        this.serviceEndpointStore = new ServiceEndpointStore();
        actionsHub.serviceEndpointActions.azureSubscriptionsUpdated.addListener(this.serviceEndpointStore.updateAzureSubscriptions);
        actionsHub.serviceEndpointActions.azureSubscriptionsStatusUpdated.addListener(this.serviceEndpointStore.updateAzureSubscriptionsStatus);
        actionsHub.serviceEndpointActions.serviceEndpointsUpdated.addListener(payload =>
            this.serviceEndpointStore.updateServiceEndpoints(payload.serviceEndpointScope, payload.serviceEndpoints)
        );
        actionsHub.serviceEndpointActions.serviceEndpointsStatusUpdated.addListener(this.serviceEndpointStore.updateServiceEndpointStatus);
        actionsHub.serviceEndpointActions.serviceEndpointAuthorized.addListener(this.serviceEndpointStore.serviceEndpointAuthorized);

        this.signalRStore = new SignalRStore();
        actionsHub.signalRActions.signalRStateUpdated.addListener(this.signalRStore.updateSignalRState);

        this.virtualMachineScaleSetStore = new VirtualMachineScaleSetStore();
        actionsHub.serviceEndpointActions.virtualMachineScaleSetsUpdated.addListener(this.virtualMachineScaleSetStore.updateVirtualMachineScaleSets);
        actionsHub.serviceEndpointActions.virtualMachineScaleSetsErrorUpdated.addListener(
            this.virtualMachineScaleSetStore.updateVirtualMachineScaleSetsError
        );
        actionsHub.serviceEndpointActions.virtualMachineScaleSetsStatusUpdated.addListener(
            this.virtualMachineScaleSetStore.updateVirtualMachineScaleSetsStatus
        );

        this.poolAnalyticsStore = new PoolAnalyticsStore();
        actionsHub.updateConfigurationValuesAction.addListener(this.poolAnalyticsStore._onUpdateConfiguration);
        actionsHub.updatePublicDataAction.addListener(this.poolAnalyticsStore._onUpdatePublicData);
        actionsHub.updatePrivateDataAction.addListener(this.poolAnalyticsStore._onUpdatePrivateData);
        actionsHub.onPrivateTrendError.addListener(this.poolAnalyticsStore._onErrorPrivate);
        actionsHub.onPublicTrendError.addListener(this.poolAnalyticsStore._onErrorPublic);

        this.topPipelinesStore = new TopPipelinesStore();
        actionsHub.onPipelineConsumptionError.addListener(this.topPipelinesStore._onError);
        actionsHub.updateTopFivePipelinesAction.addListener(this.topPipelinesStore._onUpdateData);

        this.checksStore = new ChecksStore();
        actionsHub.checksStoreStatusUpdated.addListener(this.checksStore.updateStatus);
    }
}
